import { logError } from '../../../helpers/logError'
import { ValidationResult, SetupCompleteData } from './types'

// Basic type guards
export function isString(value: unknown): value is string {
  return typeof value === 'string'
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number'
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// Helper for logging validation errors
const logValidationError = (message: string) => {
  logError(`Cardinal Validation error: ${message}`)
}

// ValidationResult type guards - only validating fields we actually use
export function isValidExtendedData(data: unknown): data is ValidationResult['data']['Payment']['ExtendedData'] {
  if (!isObject(data)) {
    logValidationError('ExtendedData validation failed: not an object')
    return false
  }

  // Only validate the fields we actually use
  const validations = [
    { field: 'CAVV', valid: isString(data.CAVV), value: data.CAVV },
    { field: 'ThreeDSVersion', valid: isString(data.ThreeDSVersion), value: data.ThreeDSVersion },
    { field: 'PAResStatus', valid: isString(data.PAResStatus), value: data.PAResStatus },
    { field: 'SignatureVerification', valid: isString(data.SignatureVerification), value: data.SignatureVerification },
  ]

  const failures = validations.filter((v) => !v.valid)
  if (failures.length > 0) {
    logValidationError('ExtendedData validation failed: ' + failures.map((f) => f.field).join(', '))
    return false
  }

  return true
}

export function isValidPayment(payment: unknown): payment is ValidationResult['data']['Payment'] {
  if (!isObject(payment)) {
    logValidationError('Payment validation failed: not an object')
    return false
  }

  if (!isValidExtendedData(payment.ExtendedData)) {
    return false
  }

  return true
}

export function isValidCardinalData(data: unknown): data is ValidationResult['data'] {
  if (!isObject(data)) {
    logValidationError('CardinalData validation failed: not an object')
    return false
  }

  if (!isString(data.ActionCode)) {
    logValidationError('CardinalData.ActionCode validation failed')
    return false
  }

  if (!isValidPayment(data.Payment)) {
    logValidationError('CardinalData.Payment validation failed')
    return false
  }

  return true
}

export function isValidSetupCompleteData(data: unknown): data is SetupCompleteData {
  if (!isObject(data)) {
    logValidationError('SetupCompleteData validation failed: not an object')
    return false
  }

  if (!isString(data.sessionId)) {
    logValidationError('SetupCompleteData.sessionId validation failed: not a string')
    return false
  }

  if (data.sessionId.length === 0) {
    logValidationError('SetupCompleteData.sessionId validation failed: empty string')
    return false
  }

  return true
}
